import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { SubGeographicalRegionService } from 'shared-lib';
import { RegionService } from '../../../services/region.service';
import { AreaService } from '../../../services/area.service';
import { UserService } from '../../../services/user.service';
import { SystemComponents } from '../../../models/role.model';
import {
  DateType,
  DynamicBuildingFilter,
  DynamicPropertiesFilter,
  EmcOptions,
  SelectableItem,
} from '../../../models/dynamic-buildings.model';

@Component({
  selector: 'app-building-filter-window',
  templateUrl: './building-filter-window.component.html',
  styleUrls: ['./building-filter-window.component.scss'],
})
export class BuildingFilterWindowComponent implements OnInit {
  @Input() filter: DynamicBuildingFilter = new DynamicBuildingFilter();
  @Input() dynamicFilterOptions: EmcOptions[] = [];
  @Input() isCimEnabled: boolean = false;

  @Output() close = new EventEmitter<void>();
  @Output() addFilter = new EventEmitter<DynamicBuildingFilter>();

  openedFilters: string[] = [];

  dateTypes = ['Last Update Time', 'Tagged Date', 'Validated Date', 'Review Date'];

  userTypes = [
    { label: 'First_Captured', value: 'first' },
    { label: 'Last_Captured', value: 'last' },
    { label: 'Reassigned', value: 'reassigned' },
    { label: 'Validated', value: 'validated' },
    { label: 'Reviewed', value: 'reviewed' },
  ];

  validationStatuses = [
    { label: 'Pending', value: 0 },
    { label: 'Complete', value: 1 },
    { label: 'Assigned', value: 3 },
    { label: 'Rejected', value: 4 },
    { label: 'Retagged', value: 5 },
  ];

  reviewStatuses = [
    { label: 'Reviewed', value: true },
    { label: 'Not Reviewed', value: false },
  ];

  tagOptions = [
    { label: 'COMMON.TAG.TAGGED', value: false },
    { label: 'COMMON.TAG.INACCESSIBLE', value: true },
  ];

  regions: any[] = [];
  allAreas: any[] = [];
  areas: any[] = [];
  geoRegions: any[] = [];
  allSubGeoRegions: any[] = [];
  subGeoRegions: any[] = [];
  allUsers: SelectableItem[] = [];

  constructor(
    private regionService: RegionService,
    private areaService: AreaService,
    private userService: UserService,
    private subGeoService: SubGeographicalRegionService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    if (this.dynamicFilterOptions.some(x => x.staticPropertyName === 'RegionDistrict')) {
      this.getRegions();
    }
    if (this.dynamicFilterOptions.some(x => x.staticPropertyName === 'GeoSubGeoRegion')) {
      this.getGeoRegions();
    }
    if (this.filter.filterParams.userType != null && this.filter.filterParams.userType !== 0) {
      this.loadUsers();
    }
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (res: any) => {
        const body = res.body;
        if (body?.status === 200 || body?.status === 'OK') {
          this.regions = body.data ?? [];
          this.getAreas();
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (res: any) => {
        const body = res.body;
        if (body?.status === 200 || body?.status === 'OK') {
          this.allAreas = body.data ?? [];
          this.updateAreas();
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  private getGeoRegions() {
    this.subGeoService.getAllForMappings().subscribe({
      next: (res: any) => {
        const body = res.body;
        if (body?.status === 200 || body?.status === 'OK') {
          const data = body.data ?? [];
          this.allSubGeoRegions = data.map((x: any) => ({
            id: x.id,
            name: x.name,
            geographicalRegionId: x.geographicalRegion?.id,
          }));
          const seen = new Set<number>();
          this.geoRegions = data
            .filter((x: any) => x.geographicalRegion?.id && !seen.has(x.geographicalRegion.id) && seen.add(x.geographicalRegion.id))
            .map((x: any) => ({ id: x.geographicalRegion.id, name: x.geographicalRegion.name }));
          this.updateSubGeoRegions();
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  toggleFilter(name: string) {
    const idx = this.openedFilters.indexOf(name);
    if (idx > -1) this.openedFilters.splice(idx, 1);
    else this.openedFilters.push(name);
  }

  onCancel() { this.close.emit(); }
  onWindowClick(event: MouseEvent) { event.stopPropagation(); }

  onAddFilter() {
    if (this.filter.filterParams.dateTo != null) {
      const d = new Date(this.filter.filterParams.dateTo);
      d.setHours(23, 59, 59, 999);
      this.filter.filterParams.dateTo = d.toISOString();
    }
    this.addFilter.emit(this.filter);
    this.onCancel();
  }

  onDateTypeChange() {
    const map: Record<string, DateType> = {
      'Last Update Time': DateType.LastUpdateTime,
      'Tagged Date': DateType.TaggedDate,
      'Validated Date': DateType.ValidatedDate,
      'Review Date': DateType.ReviewDate,
    };
    this.filter.filterParams.dateType = map[this.filter.filterParams.dateTypeString] ?? DateType.LastUpdateTime;
  }

  // ---- Region / District ----
  get filteredRegions() {
    const s = this.filter.filterParams.regionSearchText?.toLowerCase() ?? '';
    return s ? this.regions.filter(r => r.name.toLowerCase().includes(s)) : this.regions;
  }

  selectRegion(region: any) {
    const current = this.filter.filterParams.selectedRegionModel;
    this.filter.filterParams.selectedRegionModel = current?.id === region.id ? null : { id: region.id, name: region.name };
    this.filter.filterParams.selectedRegions = this.filter.filterParams.selectedRegionModel ? [region.id] : [];
    this.updateAreas();
  }

  updateAreas() {
    const s = this.filter.filterParams.areaSearchText?.toLowerCase() ?? '';
    const regionId = this.filter.filterParams.selectedRegionModel?.id;
    this.areas = this.allAreas.filter(a =>
      (!regionId || a.regionId === regionId) &&
      (!s || a.name.toLowerCase().includes(s))
    );
  }

  isAreaSelected(area: SelectableItem): boolean {
    return this.filter.filterParams.selectedAreaModels.some(a => a.id === area.id);
  }

  onAreaSelect(area: SelectableItem, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.filter.filterParams.selectedAreaModels.push(area);
    else this.filter.filterParams.selectedAreaModels = this.filter.filterParams.selectedAreaModels.filter(a => a.id !== area.id);
    this.filter.filterParams.selectedAreas = this.filter.filterParams.selectedAreaModels.map(a => a.id);
  }

  // ---- Geo / Sub-Geo ----
  get filteredGeoRegions() {
    const s = this.filter.filterParams.geoSearchText?.toLowerCase() ?? '';
    return s ? this.geoRegions.filter(g => g.name.toLowerCase().includes(s)) : this.geoRegions;
  }

  selectGeoRegion(geo: any) {
    const current = this.filter.filterParams.selectedGeoRegionModel;
    this.filter.filterParams.selectedGeoRegionModel = current?.id === geo.id ? null : { id: geo.id, name: geo.name };
    this.filter.filterParams.selectedGeographicalRegions = this.filter.filterParams.selectedGeoRegionModel ? [geo.id] : [];
    this.updateSubGeoRegions();
  }

  updateSubGeoRegions() {
    const s = this.filter.filterParams.subGeoSearchText?.toLowerCase() ?? '';
    const geoId = this.filter.filterParams.selectedGeoRegionModel?.id;
    this.subGeoRegions = this.allSubGeoRegions.filter(sg =>
      (!geoId || sg.geographicalRegionId === geoId) &&
      (!s || sg.name.toLowerCase().includes(s))
    );
  }

  isSubGeoSelected(item: SelectableItem): boolean {
    return this.filter.filterParams.selectedSubGeoModels.some(s => s.id === item.id);
  }

  onSubGeoSelect(item: SelectableItem, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.filter.filterParams.selectedSubGeoModels.push(item);
    else this.filter.filterParams.selectedSubGeoModels = this.filter.filterParams.selectedSubGeoModels.filter(s => s.id !== item.id);
    this.filter.filterParams.selectedSubgeographicalRegions = this.filter.filterParams.selectedSubGeoModels.map(s => s.id);
  }

  // ---- Users ----
  onSelectUserType(option: { label: string; value: string }) {
    const map: Record<string, number> = { first: 1, last: 2, reassigned: 3, validated: 4, audited: 5, reviewed: 6 };
    this.filter.filterParams.userType = map[option.value] ?? null;
    this.loadUsers();
  }

  private loadUsers() {
    const type = this.filter.filterParams.userType === 1 || this.filter.filterParams.userType === 2
      ? SystemComponents.Mobile
      : SystemComponents.Admin;
    this.userService.getUsersForFilter(type).subscribe({
      next: (res: any) => { this.allUsers = res?.data ?? []; },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  get filteredUsers() {
    const s = this.filter.filterParams.searchUsersText?.toLowerCase() ?? '';
    return s ? this.allUsers.filter(u => u.name.toLowerCase().includes(s)) : this.allUsers;
  }

  isUserSelected(user: SelectableItem): boolean {
    return this.filter.filterParams.selectedUserModels.some(u => u.id === user.id);
  }

  onSelectUser(user: SelectableItem, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.filter.filterParams.selectedUserModels.push(user);
    else this.filter.filterParams.selectedUserModels = this.filter.filterParams.selectedUserModels.filter(u => u.id !== user.id);
    this.filter.filterParams.selectedUsers = this.filter.filterParams.selectedUserModels.map(u => u.id);
  }

  // ---- Validation Status ----
  isValidationStatusSelected(status: number): boolean {
    return this.filter.filterParams.validationStatuses.includes(status);
  }

  onValidationStatusChange(status: number, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.filter.filterParams.validationStatuses.push(status);
    else this.filter.filterParams.validationStatuses = this.filter.filterParams.validationStatuses.filter(s => s !== status);
  }

  // ---- Dynamic attribute filters ----
  isOptionSelected(attrId: number, optionId: number): boolean {
    const df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
    return df ? df.selectedOptions.includes(optionId) : false;
  }

  onOptionChange(attrId: number, optionId: number, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    let df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
    if (!df) {
      df = new DynamicPropertiesFilter();
      df.attributeDefinitionId = attrId;
      this.filter.dynamicFilters.push(df);
    }
    if (checked) {
      if (!df.selectedOptions.includes(optionId)) df.selectedOptions.push(optionId);
    } else {
      df.selectedOptions = df.selectedOptions.filter(id => id !== optionId);
    }
    if (df.selectedOptions.length === 0 && !df.from && !df.to) {
      this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f.attributeDefinitionId !== attrId);
    }
  }

  getDynamicDateValue(attrId: number, field: 'from' | 'to'): string | null {
    const df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
    const v = df ? df[field] : null;
    return v ? new Date(v).toISOString().slice(0, 10) : null;
  }

  onDynamicDateChange(attrId: number, field: 'from' | 'to', event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const dateValue = value ? new Date(value) : null;
    let df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
    if (!df) {
      df = new DynamicPropertiesFilter();
      df.attributeDefinitionId = attrId;
      this.filter.dynamicFilters.push(df);
    }
    df[field] = dateValue;
    if (!df.from && !df.to && df.selectedOptions.length === 0) {
      this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f.attributeDefinitionId !== attrId);
    }
  }
}
